import React, { createContext, useContext, useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiText,
  EuiTitle,
  EuiSpacer,
  EuiSwitch,
  EuiAccordion,
  EuiLink,
  EuiIcon,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";

export type CookieConsent = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: number;
};

type CookieCategory = "necessary" | "analytics" | "marketing";

const STORAGE_KEY = "cookie_consent";
const CONSENT_VERSION = 1;
const CONSENT_EXPIRY_DAYS = 365;

const deleteCookie = (name: string, domain?: string) => {
  const domains = domain ? [domain, `.${domain}`] : [""];
  const paths = ["/", ""];

  domains.forEach((d) => {
    paths.forEach((p) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${p}${
        d ? `; domain=${d}` : ""
      }`;
    });
  });
};

// Consent management
export function getConsent(): CookieConsent | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const consent = JSON.parse(raw) as CookieConsent;

    const expiryDate = new Date(consent.timestamp);
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

    if (new Date() > expiryDate) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (consent.version !== CONSENT_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return consent;
  } catch {
    return null;
  }
}

export function saveConsent(consent: CookieConsent) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
}

export function clearConsent() {
  localStorage.removeItem(STORAGE_KEY);
}

export function enforceConsent(consent: CookieConsent, cookieData: any[]) {
  const cookiesToDelete: { name: string; domain?: string }[] = [];

  cookieData.forEach((cookie) => {
    const isNecessary =
      cookie.domain.includes("keycloak") || cookie.name.includes("AUTH");
    const isMarketing =
      cookie.domain.includes("microsoftonline.com") ||
      cookie.domain.includes("microsoft.com");
    const isAnalytics = !isNecessary && !isMarketing;

    if (
      (isAnalytics && !consent.analytics) ||
      (isMarketing && !consent.marketing)
    ) {
      cookiesToDelete.push({ name: cookie.name, domain: cookie.domain });
    }
  });

  cookiesToDelete.forEach(({ name, domain }) => deleteCookie(name, domain));
}

const ConsentContext = createContext<{
  consent: CookieConsent | null;
  updateConsent: (consent: CookieConsent) => void;
  hasConsent: (category: CookieCategory) => boolean;
} | null>(null);

export const ConsentProvider: React.FC<{
  children: React.ReactNode;
  cookieData?: any[];
}> = ({ children, cookieData = [] }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(() =>
    getConsent()
  );

  const updateConsent = (newConsent: CookieConsent) => {
    saveConsent(newConsent);
    setConsent(newConsent);
    enforceConsent(newConsent, cookieData);
  };

  const hasConsent = (category: CookieCategory): boolean => {
    if (!consent) return category === "necessary";
    return consent[category] === true;
  };

  return (
    <ConsentContext.Provider value={{ consent, updateConsent, hasConsent }}>
      {children}
    </ConsentContext.Provider>
  );
};

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within ConsentProvider");
  }
  return context;
}

type BannerProps = {
  privacyPolicyUrl?: string;
  companyName?: string;
  cookieData?: any[];
};

export const CookieBanner: React.FC<BannerProps> = ({
  privacyPolicyUrl = "/privacy-policy",
  companyName = "Our Company",
  cookieData = [],
}) => {
  const { translate } = useAnketaI18n();
  const { consent, updateConsent } = useConsent();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (!consent) {
      setVisible(true);
    }
  }, [consent]);

  const acceptAll = () => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };
    updateConsent(newConsent);
    setVisible(false);
  };

  const rejectAll = () => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };
    updateConsent(newConsent);
    setVisible(false);
  };

  const saveChoices = () => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics: choices.analytics,
      marketing: choices.marketing,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };
    updateConsent(newConsent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.15)",
      }}
    >
      <EuiPanel paddingSize="l" color="subdued" hasShadow={false}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <EuiFlexGroup
            alignItems="center"
            justifyContent="spaceBetween"
            gutterSize="m"
          >
            <EuiFlexItem grow={false}>
              <EuiFlexGroup alignItems="center" gutterSize="s">
                <EuiFlexItem grow={false}>
                  <EuiIcon type="documentation" size="l" />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiTitle size="s">
                    <h2 id="cookie-banner-title">
                      {translate("anketa.cookie.banner.title", {
                        defaultMessage: "Cookie Settings",
                      })}
                    </h2>
                  </EuiTitle>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>

          <EuiSpacer size="m" />

          <EuiText size="s">
            <p id="cookie-banner-desc">
              {translate("anketa.cookie.banner.intro", {
                values: { companyName },
                defaultMessage:
                  "{{companyName}} uses cookies to ensure you get the best experience on our website. Necessary cookies are required for the site to function and cannot be disabled. You can choose whether to allow other types of cookies.",
              })}
            </p>
          </EuiText>

          <EuiSpacer size="m" />

          {!showDetails ? (
            <>
              <EuiFlexGroup gutterSize="s" wrap responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={acceptAll} iconType="check">
                    {translate("anketa.cookie.banner.accept-all", {
                      defaultMessage: "Accept All",
                    })}
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={rejectAll} iconType="cross">
                    {translate("anketa.cookie.banner.reject-all", {
                      defaultMessage: "Reject All",
                    })}
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButtonEmpty
                    onClick={() => setShowDetails(true)}
                    iconType="gear"
                  >
                    {translate("anketa.cookie.banner.customize", {
                      defaultMessage: "Customize Settings",
                    })}
                  </EuiButtonEmpty>
                </EuiFlexItem>
              </EuiFlexGroup>
            </>
          ) : (
            <>
              <EuiAccordion
                id="necessary-cookies"
                buttonContent={translate(
                  "anketa.cookie.category.necessary.title",
                  { defaultMessage: "Necessary Cookies" }
                )}
                paddingSize="m"
                initialIsOpen={true}
                extraAction={
                  <EuiText size="xs" color="subdued">
                    <strong>
                      {translate("anketa.cookie.category.always-active", {
                        defaultMessage: "Always Active",
                      })}
                    </strong>
                  </EuiText>
                }
              >
                <EuiText size="s">
                  <p>
                    {translate("anketa.cookie.category.necessary.description", {
                      defaultMessage:
                        "These cookies are essential for the website to function properly. They enable core functionality such as security, authentication, and network management.",
                    })}
                  </p>
                </EuiText>
              </EuiAccordion>

              <EuiSpacer size="s" />

              <EuiAccordion
                id="analytics-cookies"
                buttonContent={translate(
                  "anketa.cookie.category.analytics.title",
                  { defaultMessage: "Analytics Cookies" }
                )}
                paddingSize="m"
                extraAction={
                  <EuiSwitch
                    label=""
                    showLabel={false}
                    aria-label={translate(
                      "anketa.cookie.category.analytics.toggle.aria",
                      { defaultMessage: "Toggle analytics cookies" }
                    )}
                    checked={choices.analytics}
                    onChange={(e) =>
                      setChoices((c) => ({ ...c, analytics: e.target.checked }))
                    }
                    compressed
                  />
                }
              >
                <EuiText size="s">
                  <p>
                    {translate("anketa.cookie.category.analytics.description", {
                      defaultMessage:
                        "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.",
                    })}
                  </p>
                </EuiText>
              </EuiAccordion>

              <EuiSpacer size="s" />

              <EuiAccordion
                id="marketing-cookies"
                buttonContent={translate(
                  "anketa.cookie.category.marketing.title",
                  { defaultMessage: "Marketing Cookies" }
                )}
                paddingSize="m"
                extraAction={
                  <EuiSwitch
                    label=""
                    showLabel={false}
                    aria-label={translate(
                      "anketa.cookie.category.marketing.toggle.aria",
                      { defaultMessage: "Toggle marketing cookies" }
                    )}
                    checked={choices.marketing}
                    onChange={(e) =>
                      setChoices((c) => ({ ...c, marketing: e.target.checked }))
                    }
                    compressed
                  />
                }
              >
                <EuiText size="s">
                  <p>
                    {translate("anketa.cookie.category.marketing.description", {
                      defaultMessage:
                        "These cookies track your browsing habits to enable us to show advertising which is more likely to be of interest to you.",
                    })}
                  </p>
                </EuiText>
              </EuiAccordion>

              <EuiSpacer size="m" />

              <EuiFlexGroup gutterSize="s">
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={saveChoices} iconType="save">
                    {translate("anketa.cookie.banner.save-choices", {
                      defaultMessage: "Save My Choices",
                    })}
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButtonEmpty
                    onClick={() => setShowDetails(false)}
                    iconType="arrowLeft"
                  >
                    {translate("anketa.cookie.banner.back", {
                      defaultMessage: "Back",
                    })}
                  </EuiButtonEmpty>
                </EuiFlexItem>
              </EuiFlexGroup>
            </>
          )}

          <EuiSpacer size="s" />

          <EuiText size="xs" color="subdued">
            <p>
              {translate("anketa.cookie.banner.learn-more-prefix", {
                defaultMessage: "Learn more in our",
              })}{" "}
              <EuiLink href={privacyPolicyUrl} target="_blank">
                {translate("anketa.cookie.banner.privacy-policy", {
                  defaultMessage: "Privacy Policy",
                })}
              </EuiLink>
              {". "}
              {translate("anketa.cookie.banner.consent-validity", {
                defaultMessage: "Your consent will be valid for 12 months.",
              })}
            </p>
          </EuiText>
        </div>
      </EuiPanel>
    </div>
  );
};

export const CookieSettingsButton: React.FC<{
  className?: string;
  iconSide?: "left" | "right";
}> = ({ className = "", iconSide = "left" }) => {
  const { translate } = useAnketaI18n();
  const openSettings = () => {
    clearConsent();
    window.location.reload();
  };

  return (
    <EuiButtonEmpty
      onClick={openSettings}
      iconType="gear"
      iconSide={iconSide}
      size="s"
      className={className}
    >
      {translate("anketa.cookie.banner.title", {
        defaultMessage: "Cookie Settings",
      })}
    </EuiButtonEmpty>
  );
};

export const ConsentStatus = () => {
  const { translate } = useAnketaI18n();
  const { consent, hasConsent } = useConsent();

  const allowedLabel = translate("anketa.cookie.status.allowed", {
    defaultMessage: "Allowed",
  });
  const blockedLabel = translate("anketa.cookie.status.blocked", {
    defaultMessage: "Blocked",
  });

  if (!consent) {
    return (
      <EuiCallOut
        title={translate("anketa.cookie.status.none-title", {
          defaultMessage: "No consent given yet",
        })}
        color="warning"
        iconType="alert"
      >
        <p>
          {translate("anketa.cookie.status.none-description", {
            defaultMessage:
              "Please configure your cookie preferences using the banner below.",
          })}
        </p>
      </EuiCallOut>
    );
  }

  return (
    <EuiCallOut
      title={translate("anketa.cookie.status.current-title", {
        defaultMessage: "Current Cookie Preferences",
      })}
      color="success"
      iconType="check"
    >
      <EuiText size="s">
        <ul>
          <li>
            <EuiIcon type="check" color="success" />{" "}
            {translate("anketa.cookie.status.necessary", {
              values: { status: allowedLabel },
              defaultMessage: "Necessary: {{status}}",
            })}
          </li>
          <li>
            <EuiIcon
              type={consent.analytics ? "check" : "cross"}
              color={consent.analytics ? "success" : "danger"}
            />{" "}
            {translate("anketa.cookie.status.analytics", {
              values: { status: consent.analytics ? allowedLabel : blockedLabel },
              defaultMessage: "Analytics: {{status}}",
            })}
          </li>
          <li>
            <EuiIcon
              type={consent.marketing ? "check" : "cross"}
              color={consent.marketing ? "success" : "danger"}
            />{" "}
            {translate("anketa.cookie.status.marketing", {
              values: { status: consent.marketing ? allowedLabel : blockedLabel },
              defaultMessage: "Marketing: {{status}}",
            })}
          </li>
        </ul>
        <EuiSpacer size="s" />
        <EuiText size="xs" color="subdued">
          {translate("anketa.cookie.status.expires", {
            defaultMessage: "Consent expires:",
          })}{" "}
          {new Date(
            consent.timestamp + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000
          ).toLocaleDateString()}
        </EuiText>
      </EuiText>
    </EuiCallOut>
  );
};
